import {
  Box,
  Button,
  DatePicker,
  FormField,
  Modal,
  SpaceBetween,
  Alert,
  Textarea,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import { useAudits } from 'src/store/audits/useAudits';
import useUserProfile from 'src/hooks/useUserProfile';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { formatToISOWithEndOfDay } from 'src/utils/generalUtils';

interface ReopenAuditModalProps {
  visible: boolean;
  onDismiss: () => void;
  auditId: string;
  businessUnitId: string;
  onSuccess?: () => void;
}

function ReopenAuditModal({
  visible,
  onDismiss,
  auditId,
  businessUnitId,
  onSuccess,
}: ReopenAuditModalProps) {
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const { reopenAudit, reopenAuditApiStatus, resetApiStatus } = useAudits();
  const { getUserId } = useUserProfile();

  useEffect(() => {
    if (reopenAuditApiStatus.isSuccess && visible) {
      onSuccess?.();
      setNewExpirationDate('');
      setReopenReason('');
      setValidationErrors({});
      resetApiStatus();
    }
  }, [reopenAuditApiStatus.isSuccess, visible, onSuccess, resetApiStatus]);

  useEffect(() => {
    if (!visible) {
      setNewExpirationDate('');
      setReopenReason('');
      setValidationErrors({});
    }
  }, [visible]);

  const handleSubmit = async () => {
    const errors: { [key: string]: string } = {};

    if (!newExpirationDate?.trim()) {
      errors.expirationDate = 'New expiration date is required';
    }

    if (!reopenReason?.trim()) {
      errors.reason = 'Reopen reason is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});

    reopenAudit({
      auditId,
      businessUnitId,
      newExpirationDate: formatToISOWithEndOfDay(newExpirationDate),
      ReOpenReason: reopenReason,
      reopenedBy: getUserId(),
    });
  };

  return (
    <Modal
      data-testid="reopen-audit-modal"
      visible={visible}
      onDismiss={onDismiss}
      header="Reopen Audit"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} data-testid="cancel-button">
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSubmit}
              loading={reopenAuditApiStatus.isLoading}
            >
              Reopen
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <SpaceBetween size="l" direction="horizontal">
          <FormField
            data-testid="expiration-date-field"
            stretch
            label={<RequiredFieldLabel label="New Expiration Date" />}
            errorText={validationErrors.expirationDate}
          >
            <DatePicker
              data-testid="expiration-date-picker"
              value={newExpirationDate}
              onChange={({ detail }) => {
                setNewExpirationDate(detail.value);
                if (validationErrors.expirationDate) {
                  setValidationErrors((prev) => ({ ...prev, expirationDate: '' }));
                }
              }}
              isDateEnabled={(date) => date > new Date(new Date().setHours(0, 0, 0, 0))}
              placeholder="mm/dd/yyyy"
            />
          </FormField>
        </SpaceBetween>
        <FormField
          data-testid="reopen-reason-field"
          label={<RequiredFieldLabel label="Reason for Reopening" />}
          errorText={validationErrors.reason}
          stretch
        >
          <Textarea
            data-testid="reopen-reason-textarea"
            value={reopenReason}
            onChange={({ detail }) => {
              setReopenReason(detail.value);
              if (validationErrors.reason) {
                setValidationErrors((prev) => ({ ...prev, reason: '' }));
              }
            }}
            placeholder="Enter reason for reopening the audit"
            rows={3}
          />
        </FormField>

        {reopenAuditApiStatus.errorMessage && (
          <Alert data-testid="error-alert" type="error">
            {reopenAuditApiStatus.errorMessage}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}

export default ReopenAuditModal;
